/**
 * <gwt-zoomable-image>
 * Accessible, dependency-free zoomable image with full-screen dialog/pinch-zoom/pan.
 * Props:
 *  - src   : required (full image)
 *  - thumb : optional (inline preview); falls back to src
 *  - alt   : forwarded to <img> and used for aria labels
 *  - badge : "true" | "false" (show zoom badge on thumbnail)
 *  - thumb-width  : number (default 200)
 *  - thumb-height : number (default 200)
 */
(() => {
  if (customElements.get('gwt-zoomable-image')) return;

  const template = document.createElement('template');
  template.innerHTML = `
    <style>
      :host {
        display: inline-block;
        position: relative;
        line-height: 0;
        cursor: zoom-in;
        --gwt-badge-bg: rgba(0,0,0,.55);
        --gwt-badge-fg: #fff;
        --gwt-badge-size: 28px;
        --gwt-btn-bg: rgba(0,0,0,.55);
        --gwt-btn-fg: #fff;
        --gwt-btn-radius: 10px;
        --gwt-btn-gap: 8px;
        --gwt-shadow: 0 10px 30px rgba(0,0,0,.35);
        --gwt-backdrop: rgba(0,0,0,.9);
        --gwt-accent: #00ff88;
      }
      .thumb {
        display:block;
        max-width:100%;
        height:auto;
        border-radius: inherit;
        border:inherit;
      }
      .badge {
        position:absolute;
        top:8px; right:8px;
        width:var(--gwt-badge-size); height:var(--gwt-badge-size);
        border-radius: 50%;
        background: var(--gwt-badge-bg);
        color: var(--gwt-badge-fg);
        display:flex; align-items:center; justify-content:center;
        pointer-events:none;
        box-shadow: var(--gwt-shadow);
      }
      .badge svg { width: 60%; height: 60%; }

      dialog {
        border:0; padding:0; margin:0; width:100vw; height:100vh;
        background: transparent;
      }
      dialog::backdrop { background: var(--gwt-backdrop); }
      .stage {
        position:fixed; inset:0;
        display:grid; place-items:center;
        touch-action: none; /* implement our own gestures */
      }
      .canvas-wrap {
        position: relative;
        max-width: 100vw;
        max-height: 100vh;
        overflow: hidden;
        cursor: grab;
      }
      .canvas-wrap:active { cursor: grabbing; }
      .full {
        user-select:none;
        -webkit-user-drag: none;
        transform-origin: 0 0;
        will-change: transform;
        display:block;
        max-width:none;
      }
      .ui {
        position: fixed;
        left: 50%;
        transform: translateX(-50%);
        bottom: 20px;
        display: flex; gap: var(--gwt-btn-gap);
        z-index: 2;
      }
      .btn {
        background: var(--gwt-btn-bg);
        color: var(--gwt-btn-fg);
        border: 1px solid rgba(255,255,255,.15);
        padding: 10px 12px;
        border-radius: var(--gwt-btn-radius);
        display:flex; align-items:center; gap:6px;
        font: 600 14px/1.1 system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
        box-shadow: var(--gwt-shadow);
      }
      .btn:focus-visible { outline: 2px solid var(--gwt-accent); outline-offset: 2px; }
      .topbar {
        position: fixed; top: 12px; right: 12px; display:flex; gap: var(--gwt-btn-gap);
        z-index: 2;
      }
      @media (prefers-reduced-motion: reduce) {
        .full { transition: none !important; }
      }
    </style>

    <button class="trigger" part="trigger" type="button"
            aria-haspopup="dialog" aria-expanded="false" style="all:unset;display:block;">
      <img class="thumb" part="thumbnail" />
      <div class="badge" part="badge" hidden aria-hidden="true" title="Zoom image">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor"
          d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79L20 21.49 21.49 20 15.5 14Zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14Z"/></svg>
      </div>
    </button>

    <dialog class="dlg" part="dialog" aria-label="Image viewer">
      <div class="stage" role="document">
        <div class="topbar">
          <button class="btn btn-close" type="button" aria-label="Close (Esc)">✕</button>
        </div>
        <div class="canvas-wrap" part="canvas">
          <img class="full" part="image" />
        </div>
        <div class="ui">
          <button class="btn btn-zoom-in" type="button" aria-label="Zoom in">＋</button>
          <button class="btn btn-zoom-out" type="button" aria-label="Zoom out">－</button>
          <button class="btn btn-fit" type="button" aria-label="Fit to screen">Fit</button>
          <button class="btn btn-1x" type="button" aria-label="Actual size (1:1)">1×</button>
          <button class="btn btn-fs" type="button" aria-label="Toggle Fullscreen">⤢</button>
        </div>
      </div>
    </dialog>
  `;

  class GwtZoomableImage extends HTMLElement {
    static get observedAttributes() { return ['src','thumb','alt','badge','thumb-width','thumb-height']; }

    #els = {};
    #state = { scale: 1, minScale: 1, maxScale: 8, x: 0, y: 0 };
    #drag = { active:false, startX:0, startY:0, startTx:0, startTy:0 };
    #pinch = { active:false, startDist:0, startScale:1, mid:[0,0] };

    constructor() {
      super();
      const root = this.attachShadow({ mode: 'open' });
      root.appendChild(template.content.cloneNode(true));
      const $ = (sel) => root.querySelector(sel);
      this.#els = {
        trigger: $('.trigger'),
        thumb: $('.thumb'),
        badge: $('.badge'),
        dlg: $('.dlg'),
        stage: $('.stage'),
        canvas: $('.canvas-wrap'),
        full: $('.full'),
        btnIn: $('.btn-zoom-in'),
        btnOut: $('.btn-zoom-out'),
        btnFit: $('.btn-fit'),
        btn1x: $('.btn-1x'),
        btnFs: $('.btn-fs'),
        btnClose: $('.btn-close'),
      };
    }

    connectedCallback() {
      // Thumbnail setup
      const tw = Number(this.getAttribute('thumb-width') || 200);
      const th = Number(this.getAttribute('thumb-height') || 200);
      this.#els.thumb.width = tw;
      this.#els.thumb.height = th;
      this.#els.thumb.style.objectFit = 'contain';

      this.#els.thumb.alt = this.getAttribute('alt') || '';
      this.#els.thumb.src = this.getAttribute('thumb') || this.getAttribute('src') || '';
      if (this.getAttribute('badge') === 'true') this.#els.badge.hidden = false;

      // Open dialog
      this.#els.trigger.addEventListener('click', () => this.open());
      this.#els.trigger.tabIndex = 0;
      this.#els.trigger.setAttribute('role', 'button');
      this.#els.trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.open(); }
      });

      // Dialog controls
      this.#els.btnClose.addEventListener('click', () => this.close());
      this.#els.btnIn.addEventListener('click', () => this.zoomBy(1.2));
      this.#els.btnOut.addEventListener('click', () => this.zoomBy(1/1.2));
      this.#els.btnFit.addEventListener('click', () => this.fit());
      this.#els.btn1x.addEventListener('click', () => this.setScale(1, true));
      this.#els.btnFs.addEventListener('click', () => this.toggleFullscreen());
      this.#els.dlg.addEventListener('close', () => {
        this.#els.trigger.setAttribute('aria-expanded', 'false');
        this.exitFullscreenIfAny();
      });

      // Wheel zoom
      this.#els.dlg.addEventListener('wheel', (e) => {
        e.preventDefault();
        const delta = e.deltaY < 0 ? 1.08 : 1/1.08;
        this.zoomAt(delta, e.clientX, e.clientY);
      }, { passive:false });

      // Drag to pan
      const onPointerDown = (e) => {
        e.preventDefault();
        this.#els.canvas.setPointerCapture(e.pointerId);
        this.#drag.active = true;
        this.#drag.startX = e.clientX;
        this.#drag.startY = e.clientY;
        this.#drag.startTx = this.#state.x;
        this.#drag.startTy = this.#state.y;
      };
      const onPointerMove = (e) => {
        if (!this.#drag.active || this.#pinch.active) return;
        this.#state.x = this.#drag.startTx + (e.clientX - this.#drag.startX);
        this.#state.y = this.#drag.startTy + (e.clientY - this.#drag.startY);
        this.applyTransform(true); // paint only
      };
      const onPointerUp = (e) => {
        if (!this.#drag.active) return;
        this.#drag.active = false;
        this.#els.canvas.releasePointerCapture(e.pointerId);
        this.clampPan();           // clamp once at end
        this.applyTransform(true); // paint
      };
      this.#els.canvas.addEventListener('pointerdown', onPointerDown);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);

      // Pinch zoom
      this.#els.canvas.addEventListener('touchstart', (e) => {
        if (e.touches.length === 2) {
          this.#pinch.active = true;
          this.#pinch.startDist = this.distance(e.touches[0], e.touches[1]);
          this.#pinch.startScale = this.#state.scale;
          this.#pinch.mid = this.midpoint(e.touches[0], e.touches[1]);
        }
      }, { passive: true });
      this.#els.canvas.addEventListener('touchmove', (e) => {
        if (this.#pinch.active && e.touches.length === 2) {
          const newDist = this.distance(e.touches[0], e.touches[1]);
          const factor = newDist / this.#pinch.startDist;
          this.setScale(this.#pinch.startScale * factor, true, this.#pinch.mid[0], this.#pinch.mid[1]);
        }
      }, { passive: true });
      this.#els.canvas.addEventListener('touchend', () => { this.#pinch.active = false; }, { passive: true });

      // Keyboard inside dialog
      this.#els.dlg.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === '+') this.zoomBy(1.2);
        if (e.key === '-') this.zoomBy(1/1.2);
        if (e.key === '0') this.fit();
        if (e.key === '1') this.setScale(1, true);
        const step = 40;
        if (['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(e.key)) {
          if (e.key === 'ArrowLeft')  this.#state.x += step;
          if (e.key === 'ArrowRight') this.#state.x -= step;
          if (e.key === 'ArrowUp')    this.#state.y += step;
          if (e.key === 'ArrowDown')  this.#state.y -= step;
          this.clampPan();
          this.applyTransform(true);
        }
      });
    }

    attributeChangedCallback(name, _old, value) {
      if (!this.isConnected) return;
      if (name === 'thumb' || name === 'src') {
        this.#els.thumb.src = this.getAttribute('thumb') || this.getAttribute('src') || '';
      }
      if (name === 'alt') {
        this.#els.thumb.alt = value || '';
      }
      if (name === 'badge') {
        this.#els.badge.hidden = value !== 'true';
      }
      if (name === 'thumb-width' || name === 'thumb-height') {
        const tw = Number(this.getAttribute('thumb-width') || 200);
        const th = Number(this.getAttribute('thumb-height') || 200);
        this.#els.thumb.width = tw;
        this.#els.thumb.height = th;
        
      }
    }

    /* ---------- Public API ---------- */
    open() {
      this.#els.full.alt = this.getAttribute('alt') || '';
      this.#els.full.src = this.getAttribute('src') || this.getAttribute('thumb') || '';
      this.#els.dlg.showModal();
      this.#els.trigger.setAttribute('aria-expanded', 'true');
      this.#els.full.decode?.().catch(()=>{}).finally(() => this.fit());
      this.#els.btnClose.focus({ preventScroll: true });
    }
    close() { if (this.#els.dlg.open) this.#els.dlg.close(); }

    toggleFullscreen() {
      const el = this.#els.dlg;
      if (!document.fullscreenElement) el.requestFullscreen?.();
      else document.exitFullscreen?.();
    }
    exitFullscreenIfAny() { if (document.fullscreenElement) document.exitFullscreen?.(); }

    fit() {
      const img = this.#els.full;
      const wrap = this.#els.canvas.getBoundingClientRect();
      const naturalW = img.naturalWidth || img.width || 1;
      const naturalH = img.naturalHeight || img.height || 1;

      const scale = Math.min(wrap.width / naturalW, wrap.height / naturalH) || 1;
      this.#state.minScale = Math.min(1, scale);
      this.#state.scale = scale;
      this.#state.x = (wrap.width - naturalW * scale) / 2;
      this.#state.y = (wrap.height - naturalH * scale) / 2;

      this.applyTransform(true); // paint only
    }

    setScale(next, clamp = true, cx = null, cy = null) {
      const prev = this.#state.scale;
      const min = Math.min(this.#state.minScale, 1);
      const max = this.#state.maxScale;
      let s = clamp ? Math.max(min, Math.min(max, next)) : next;

      if (cx != null && cy != null) {
        const beforeX = (cx - this.#state.x) / prev;
        const beforeY = (cy - this.#state.y) / prev;
        this.#state.x = cx - beforeX * s;
        this.#state.y = cy - beforeY * s;
      }

      this.#state.scale = s;
      this.clampPan();           // clamp first
      this.applyTransform(true); // then paint
    }

    zoomBy(factor) {
      const rect = this.#els.canvas.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      this.setScale(this.#state.scale * factor, true, cx, cy);
    }

    zoomAt(factor, cx, cy) {
      this.setScale(this.#state.scale * factor, true, cx, cy);
    }

    clampPan() {
      const img = this.#els.full;
      const wrap = this.#els.canvas.getBoundingClientRect();
      const w = (img.naturalWidth || img.width || 1) * this.#state.scale;
      const h = (img.naturalHeight || img.height || 1) * this.#state.scale;

      // If image smaller than viewport in a dimension, keep centered in that axis.
      const minX = w <= wrap.width  ? (wrap.width - w) / 2  : wrap.width - w;
      const maxX = w <= wrap.width  ? (wrap.width - w) / 2  : 0;
      const minY = h <= wrap.height ? (wrap.height - h) / 2 : wrap.height - h;
      const maxY = h <= wrap.height ? (wrap.height - h) / 2 : 0;

      this.#state.x = Math.min(maxX, Math.max(minX, this.#state.x));
      this.#state.y = Math.min(maxY, Math.max(minY, this.#state.y));
    }

    applyTransform(avoidClamp = true) {
      // Paint only; DO NOT call clampPan() here to avoid recursion.
      const { x, y, scale } = this.#state;
      const img = this.#els.full;
      img.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    }

    distance(t1, t2) {
      const dx = t1.clientX - t2.clientX;
      const dy = t1.clientY - t2.clientY;
      return Math.hypot(dx, dy);
    }
    midpoint(t1, t2) {
      return [(t1.clientX + t2.clientX)/2, (t1.clientY + t2.clientY)/2];
    }
  }

  customElements.define('gwt-zoomable-image', GwtZoomableImage);
})();